import { useMemo, useRef, useState, useEffect } from 'react';
import type { FC } from 'react';
import { Card, Typography, Form, Input, DatePicker, Radio, Space, Divider, Row, Col, Checkbox, message, Table, Tooltip, Modal } from 'antd';
import { PlusOutlined, CloseOutlined, MinusOutlined, CheckSquareOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetQaqcPipeRfiForms, useGetPersonnel } from '../../../../../hooks/genericHooks';
import PersonnelSelect from '../../../../../components/modals/PersonnelSelect';
import type { QaqcPipeRfiForms } from '../../../../../types';
import { useAuth } from '../../../../../components/common/security/AuthContext';
import { insertData, upsertHeaderWithItems, getNextSequenceValue, getTableNextSequenceValue, fetchSqlData, fetchSqlDataWithQuery } from '../../../../../services/api';
import { useQueryClient } from '@tanstack/react-query';
import type { QaqcPipeInspectionRecordCreate } from '../../../../../types';
import FileUploadTool, { FileUploadToolHandle } from '../../../../../components/common/FileUploadTool';
import PipeInspectionForm from './components/PipeInspectionForm';
import { deriveHeatNos, mergeVcOverlay } from './components/inspection-form/inspectionPayloadCodec';
import Button from '../../../../../components/common/Button';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const dayjs: any = require('dayjs');

const { Title, Text } = Typography;

// Shared width for section headers inside the BC expanded panel
const SECTION_HEADER_WIDTH = 80;

const QaqcPipeInspectionCreate: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const passedRow = (location.state as any)?.row as QaqcPipeRfiForms | undefined;
  const { user } = useAuth();
  // Issuer defaults from localStorage, with fallbacks to auth user
  const issuer_id: string = (typeof window !== 'undefined' && localStorage.getItem('personnel_id')) || '';
  const issuer_name: string =
    (typeof window !== 'undefined' && (localStorage.getItem('personnel_name') || localStorage.getItem('full_name') || localStorage.getItem('username'))) ||
    ((user as any)?.full_name || (user as any)?.name || (user as any)?.username || '');

  const { data, isLoading } = useGetQaqcPipeRfiForms();
  const all: QaqcPipeRfiForms[] = data?.data || [];
  const { data: personnelData } = useGetPersonnel();

  const rfi = useMemo(() => {
    if (passedRow && (!id || String(passedRow.id) === String(id))) return passedRow;
    return all.find(r => String(r.id) === String(id));
  }, [all, id, passedRow]);

  const [form] = Form.useForm();
  const [reservedSeq, setReservedSeq] = useState<number | null>(null);
  const seqReservedRef = useRef<boolean>(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const frConfirmShownRef = useRef<boolean>(false);
  const fileUploadRef = useRef<FileUploadToolHandle>(null);
  const queryClient = useQueryClient();

  // Map weldingdata_id -> weld_no_rev from RFI (if present and aligned)
  const weldRevMap = useMemo(() => {
    const map: Record<string, number | null> = {};
    const ids = (rfi as any)?.weldingdata_ids as (string | number)[] | undefined;
    const revs = (rfi as any)?.weld_no_revs as (number | null)[] | undefined;
    if (Array.isArray(ids) && Array.isArray(revs)) {
      ids.forEach((id, idx) => {
        map[String(id)] = revs[idx] ?? null;
      });
    }
    return map;
  }, [rfi]);

  // Align issuer defaulting with RFI Request page: resolve from personnel list by localStorage personnel_id
  useEffect(() => {
    const currentPid = (typeof window !== 'undefined' && localStorage.getItem('personnel_id')) || '';
    if (!currentPid) return;
    const list = ((personnelData as any)?.data || []) as any[];
    if (!Array.isArray(list) || list.length === 0) return;
    const me = list.find((p: any) => String(p.pers_id || '') === currentPid || String(p.id || '') === currentPid);
    const fullName = me ? `${me.first_name || ''} ${me.last_name || ''}`.trim() : undefined;
    form.setFieldsValue({
      issuer: fullName || form.getFieldValue('issuer'),
      issuer_id: currentPid || form.getFieldValue('issuer_id'),
    });
  }, [personnelData, form]);

  const inspectionType = rfi?.inspection_type || 'BC';
  const requestDate = rfi?.request_date ? dayjs(rfi.request_date) : undefined;
  const invitees = Array.isArray(rfi?.invitees) ? (rfi!.invitees as string[]) : [];
  const appointees = Array.isArray((rfi as any)?.appointees) ? ((rfi as any).appointees as string[]) : [];
  const baseAttendees = useMemo(() => Array.from(new Set([...(appointees || []), ...(invitees || [])])), [appointees, invitees]);
  const [extraAttendees, setExtraAttendees] = useState<string[]>([]);
  const attendeeOptions = useMemo(
    () => Array.from(new Set([...(baseAttendees || []), ...(extraAttendees || [])])),
    [baseAttendees, extraAttendees]
  );
  const [attModalOpen, setAttModalOpen] = useState(false);
  const removeExtraAttendee = (name: string) => {
    setExtraAttendees(prev => prev.filter(n => n !== name));
    const cur: string[] = form.getFieldValue('attendance') || [];
    if (Array.isArray(cur) && cur.includes(name)) {
      form.setFieldsValue({ attendance: cur.filter(n => n !== name) });
    }
  };
  const weldIds = Array.isArray((rfi as any)?.weldingdata_ids) ? ((rfi as any).weldingdata_ids as (string | number)[]) : [];

  // Build per-weld metadata map from RFI enriched arrays
  const weldMetaMap = useMemo(() => {
    const map: Record<string, {
      pipe_size?: string | null,
      service_class_name?: string | null,
      weldtype_name?: string | null,
      spool_id?: number | null,
      weld_category?: string | null,
      code_no?: string | null,
      fluid?: string | null,
    }> = {};
    const ids = (rfi as any)?.weldingdata_ids as (string | number)[] | undefined;
    if (!Array.isArray(ids)) return map;
    const asStr = (v: any) => (v == null ? null : String(v));
    const toNumOrNull = (v: any) => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    
    const pipe_sizes: (string | null)[] = Array.isArray((rfi as any)?.pipe_sizes) ? (rfi as any).pipe_sizes : [];
    const service_class_names: (string | null)[] = Array.isArray((rfi as any)?.service_class_names) ? (rfi as any).service_class_names : [];
    const spool_ids: (number | null)[] = Array.isArray((rfi as any)?.spool_ids) ? (rfi as any).spool_ids : [];
    const weld_categories: (string | null)[] = Array.isArray((rfi as any)?.weld_categories) ? ((rfi as any).weld_categories) : [];
    const code_nos: (string | null)[] = Array.isArray((rfi as any)?.code_nos) ? (rfi as any).code_nos : [];
    const weldtype_names: (string | null)[] = Array.isArray((rfi as any)?.weldtype_names) ? (rfi as any).weldtype_names : [];
    const fluids: (string | null)[] = Array.isArray((rfi as any)?.fluids) ? (rfi as any).fluids : [];
    ids.forEach((id, idx) => {
      const key = String(id);
      map[key] = {
        pipe_size: asStr(pipe_sizes[idx] ?? null),
        service_class_name: asStr(service_class_names[idx] ?? null),
        weldtype_name: asStr(weldtype_names[idx] ?? null),
        spool_id: toNumOrNull(spool_ids[idx] ?? null),
        weld_category: asStr(weld_categories[idx] ?? null),
        code_no: asStr(code_nos[idx] ?? null),
        fluid: asStr(fluids[idx] ?? null),
      };
    });
    return map;
  }, [rfi]);

  const disabledDate = (current: Dayjs) => {
    if (!requestDate) return false;
    return current.isBefore(requestDate, 'day');
  };

  // Reserve sequence exactly once on mount (StrictMode-safe via ref). Do not depend on changing data.
  useEffect(() => {
    const run = async () => {
      try {
        if (seqReservedRef.current) return;
        seqReservedRef.current = true; // guard before async to avoid double-consume in StrictMode
        const nextId = await getNextSequenceValue('public.qaqc_pipe_inspection_records_id_seq');
        setReservedSeq(nextId ?? 1);
        const serial9 = String(nextId ?? 1).padStart(9, '0');
        form.setFieldsValue({
          report_no_serial: serial9,
        });
      } catch {}
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the prefix in sync with inspection type without touching reserved sequence
  useEffect(() => {
    const inspType = String(inspectionType || (rfi as any)?.inspection_type || '').toUpperCase();
    form.setFieldsValue({ report_no_prefix: `PIR-${inspType}-` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionType, rfi]);

  const onFinish = async (values: any) => {
    // Use the reserved sequence consumed on mount; fallback to allocate now if missing
    let seq = reservedSeq;
    if (!seq) {
      seq = await getNextSequenceValue('public.qaqc_pipe_inspection_records_id_seq');
      setReservedSeq(seq ?? null);
    }
    const serial9 = String(seq ?? 1).padStart(9, '0');
    const inspType = String(inspectionType || (rfi as any)?.inspection_type || '').toUpperCase();
    const report_id = `PIR-${inspType}-${serial9}`;
    const weldingdata_ids = (weldIds || []).map((w) => String(w));

    // Use derived flags and remarks from shared form
    const hasFailOrRework: boolean = Boolean(values?.has_fail_or_rework);
    const combinedRemarks: string = String(values?.combined_remarks || '').trim();
    const parentNotes = (values?.report_notes ? String(values.report_notes).trim() : '');
    if (hasFailOrRework && !combinedRemarks && !parentNotes) {
      message.warning('Please provide an explanation for FAIL/REWORK in Notes or detailed remarks before saving.');
      return;
    }
    if (hasFailOrRework && (combinedRemarks || parentNotes) && !frConfirmShownRef.current) {
      Modal.confirm({
        title: 'Confirm remarks explain FAIL/REWORK',
        content: 'You selected FAIL/REWORK. Do your Notes and/or collected detailed remarks sufficiently explain the reason?',
        okText: 'Yes, Continue',
        cancelText: 'Edit Remarks',
        onOk: () => {
          frConfirmShownRef.current = true;
          form.submit();
        },
      });
      return;
    }
    const pwht_hardness: number[] | undefined = Array.isArray(values?.pwht_hardness_list) && values.pwht_hardness_list.length
      ? values.pwht_hardness_list
      : undefined;
    // Validation using shared flags
    if (inspectionType === 'PWHT' && values?.pwht_hardness_out_of_range) {
      const ids = Array.isArray(values?.pwht_hardness_invalid_welds) ? values.pwht_hardness_invalid_welds : [];
      message.error(`PWHT hardness out of range for weld(s): ${ids.join(', ')}`);
      return;
    }
    // Compose final notes from shared suggestion
    const finalNotes = values?.final_notes;

    // Build results array aligned with weldingdata_ids (fallback to 'P')
    const inputResults: any[] = Array.isArray(values?.results_codes) ? values.results_codes : [];
    const resultsArr: string[] = (weldingdata_ids || []).map((_, idx) => {
      const v = inputResults[idx];
      if (v === undefined || v === null || v === '') return 'P';
      const s = String(v).trim();
      return s ? s : 'P';
    });

    const header_data: any = {
      report_id,
      issued_by: values.issuer,
      report_date: values.report_date?.format ? values.report_date.format('YYYY-MM-DD') : String(values.report_date),
      rfi_id: String((rfi as any)?.form_id || ''),
      report_notes: finalNotes,
      report_status: 'draft',
      attendees: Array.isArray(values.attendance) ? values.attendance : [],
      attendee_ids: undefined,
      work_permit_ref_num: (rfi as any)?.work_permit_ref_num || undefined,
      work_permit_description: (rfi as any)?.work_permit_description || undefined,
      inspection_type: inspectionType as any,
      pwht_hardness,
      weldingdata_ids,
      results: resultsArr,
      weld_no_revs: (weldingdata_ids || []).map((wid) => weldRevMap[String(wid)] ?? null),
    };

    // Use the form-provided per-weld payloads as source of truth
    const detailsPayloads: any[] = Array.isArray(values?.details_payloads) ? values.details_payloads : [];
    // Derive header-level heat lists (aligned with weldingdata_ids order)
    const { heat_no1s, heat_no2s } = deriveHeatNos(weldingdata_ids.map((wid, idx) => detailsPayloads[idx] || {}));
    if (String(inspectionType).toUpperCase() === 'BC') {
      header_data.heat_no1s = heat_no1s;
      header_data.heat_no2s = heat_no2s;
    }

    // Simple validation: ensure result is among PASS/FAIL/REWORK
    for (const wid of weldingdata_ids) {
      const r = values?.results?.[wid];
      const res = r?.result;
      if (res && res !== 'PASS' && res !== 'FAIL' && res !== 'REWORK') {
        message.error(`Invalid result for weld ${wid}`);
        return;
      }
    }

    // Prepare explicit items columns/values (use stringified payload). Do insert-only (no conflict keys)
    const items_columns = ['inspection_report_id','weldingdata_id','weld_no_rev','payload','pipe_size','service_class_name','weldtype_name','spool_id','weld_category','code_no','fluid'];
    const getWelderStr = (wid: string, layer: 'flw'|'fw') => {
      const v = form.getFieldValue(['results', wid, 'vc', `${layer}_welder`]);
      const s = (v != null ? String(v) : '').trim();
      return s;
    };
    const getDateStr = (wid: string, key: 'flw_date'|'fw_date') => {
      const v = form.getFieldValue(['results', wid, 'vc', key]);
      const s = v?.format ? v.format('YYYY-MM-DD') : (v ? String(v) : '').trim();
      return s;
    };
    const items_values: any[][] = weldingdata_ids.map((wid, idx) => {
      const base = (detailsPayloads[idx] !== undefined && detailsPayloads[idx] !== null) ? detailsPayloads[idx] : {};
      const baseVc = (base && typeof base === 'object' && (base as any).vc && typeof (base as any).vc === 'object') ? (base as any).vc : {};
      const flw = (() => { const s = getWelderStr(wid, 'flw'); if (s) return s; const b = (baseVc?.flw_welder != null ? String(baseVc.flw_welder) : '').trim(); return b || ''; })();
      const fw = (() => { const s = getWelderStr(wid, 'fw');  if (s) return s; const b = (baseVc?.fw_welder  != null ? String(baseVc.fw_welder)  : '').trim(); return b || ''; })();
      const flwDate = (() => { const s = getDateStr(wid, 'flw_date'); if (s) return s; const b = (baseVc?.flw_date != null ? String(baseVc.flw_date) : '').trim(); return b || ''; })();
      const fwDate  = (() => { const s = getDateStr(wid, 'fw_date');  if (s) return s; const b = (baseVc?.fw_date  != null ? String(baseVc.fw_date)  : '').trim(); return b || ''; })();
      const merged = mergeVcOverlay(base, { flw, fw, flwDate, fwDate });
      const payloadStr = (() => { try { return JSON.stringify(merged || {}); } catch { return '{}'; } })();
      const meta = weldMetaMap[String(wid)] || {} as any;
      const spoolId = (() => {
        const v = meta.spool_id as any;
        if (v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      })();
      return [
        header_data.report_id,
        wid,
        weldRevMap[String(wid)] ?? null,
        payloadStr,
        meta.pipe_size ?? null,
        meta.service_class_name ?? null,
        meta.weldtype_name ?? null,
        spoolId,
        meta.weld_category ?? null,
        meta.code_no ?? null,
        meta.fluid ?? null,
      ];
    });

    // Derive header-level welders arrays (joined strings per weld, aligned with weldingdata_ids)
    const flw_welders: (string | null)[] = weldingdata_ids.map((wid, idx) => {
      const s1 = getWelderStr(wid, 'flw');
      if (s1) return s1;
      const p = detailsPayloads[idx] || {};
      const vc = (p && typeof p === 'object') ? (p.vc || p) : {};
      const s2 = (vc.flw_welder != null ? String(vc.flw_welder) : '').trim();
      return s2 ? s2 : null;
    });
    const fw_welders: (string | null)[] = weldingdata_ids.map((wid, idx) => {
      const s1 = getWelderStr(wid, 'fw');
      if (s1) return s1;
      const p = detailsPayloads[idx] || {};
      const vc = (p && typeof p === 'object') ? (p.vc || p) : {};
      const s2 = (vc.fw_welder != null ? String(vc.fw_welder) : '').trim();
      return s2 ? s2 : null;
    });
    const flw_dates: (string | null)[] = weldingdata_ids.map((wid, idx) => {
      const s1 = getDateStr(wid, 'flw_date');
      if (s1) return s1;
      const p = detailsPayloads[idx] || {};
      const vc = (p && typeof p === 'object') ? (p.vc || p) : {};
      const s2 = (vc.flw_date != null ? String(vc.flw_date) : '').trim();
      return s2 ? s2 : null;
    });
    const fw_dates: (string | null)[] = weldingdata_ids.map((wid, idx) => {
      const s1 = getDateStr(wid, 'fw_date');
      if (s1) return s1;
      const p = detailsPayloads[idx] || {};
      const vc = (p && typeof p === 'object') ? (p.vc || p) : {};
      const s2 = (vc.fw_date != null ? String(vc.fw_date) : '').trim();
      return s2 ? s2 : null;
    });
    if (String(inspectionType).toUpperCase() === 'VC') {
      header_data.flw_welders = flw_welders;
      header_data.fw_welders = fw_welders;
      header_data.flw_dates = flw_dates;
      header_data.fw_dates = fw_dates;
    }
    try {
      if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
        const up = await fileUploadRef.current.finalizeUpload(report_id);
        if (!up.success) {
          message.error(up.error || 'File upload failed. Report not saved.');
          return;
        }
        header_data.upload_path = up.folderPath || null;
      }
      const resp = await upsertHeaderWithItems({
        header_table: 'qaqc_pipe_inspection_records',
        header_data,
        items_table: 'qaqc_pipe_insp_rcrd_details',
        items_columns,
        items_values,
        items_conflict_keys: [],
        items_update_columns: [],
      });
      // Verify insert actually persisted using standard endpoint (no raw SQL requirement)
      const hdrRows = await fetchSqlData('qaqc_pipe_inspection_records', 'report_id', report_id);
      const detRows = await fetchSqlData('qaqc_pipe_insp_rcrd_details', 'inspection_report_id', report_id);
      const hdrOk = Array.isArray(hdrRows) && hdrRows.length > 0;
      const detOk = Array.isArray(detRows) && detRows.length === items_values.length;
      if (!hdrOk || !detOk) {
        message.error('Insert did not persist. Please retry.');
        return;
      }
      try { await queryClient.invalidateQueries({ queryKey: ['qaqc_pipe_inspection_records'] }); } catch {}
      message.success('Inspection report created (with details)');
      navigate('/qaqc/ndt/reports');
    } catch (e: any) {
      message.error('Failed to create inspection report');
    }
  };

  if (!rfi && !isLoading) {
    return (
      <Card>
        <Title level={3}>Inspection Create</Title>
        <Text type="danger">RFI not found.</Text>
      </Card>
    );
  }

  return (
    <Card
      loading={isLoading}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            <a onClick={() => navigate(-1)} style={{ marginRight: 16, fontSize: 18 }}>&larr; Back</a>
            PIPE Inspection Report
          </span>
        </div>
      }
      style={{ maxWidth: 800, margin: '0 auto' }}
    >

      <Form form={form} layout="vertical" size="small" onFinish={onFinish} initialValues={{
        issuer: '',
        issuer_id: issuer_id,
        report_no_prefix: `PIR-${inspectionType}-`,
        report_date: dayjs().isBefore(requestDate) && requestDate ? requestDate : dayjs(),
        rfi_no: rfi?.form_id ?? rfi?.id,
        report_notes: '',
        attendance: Array.from(new Set([
          ...((rfi as any)?.appointees || []),
          ...((rfi as any)?.invitees || []),
        ].filter(Boolean))),
        results: (weldIds || []).reduce((acc: any, w) => { acc[String(w)] = { result: 'PASS', hardness: undefined }; return acc; }, {}),
        weldingdata_ids: (weldIds || []).map((w) => String(w)),
      }}>
        {/* Hidden issuer id for submit consistency */}
        <Form.Item name="issuer_id" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="weldingdata_ids" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Row gutter={[8, 0]}>
          <Col xs={24} md={7}>
            <Form.Item label="Report Number">
              <Input.Group compact>
                <Form.Item name="report_no_prefix" noStyle>
                  <Input style={{ width: '45%' }} readOnly />
                </Form.Item>
                <Form.Item name="report_no_serial" noStyle>
                  <Input style={{ width: '55%' }} readOnly />
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </Col>
          <Col xs={18} md={6}>
            <Form.Item label="Issuer" name="issuer">
              <Input readOnly />
            </Form.Item>
          </Col>
          <Col xs={15} md={5}>
            <Form.Item label="Report Date" name="report_date" rules={[{ required: true, message: 'Select date' }]}> 
              <DatePicker style={{ width: '100%' }} disabledDate={disabledDate} />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item label="RFI No" name="rfi_no">
              <Input readOnly />
            </Form.Item>
          </Col>
        </Row>

        <PipeInspectionForm
          form={form}
          mode="create"
          inspectionType={inspectionType}
          weldingdataIds={weldIds}
          weldRevMap={weldRevMap}
          fileUploadRef={fileUploadRef}
          weldMetaMap={weldMetaMap}
        />

        <Space style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button action="add" type="primary" htmlType="submit">Save Report</Button>
        </Space>
      </Form>
    </Card>
  );
};

export default QaqcPipeInspectionCreate;
